import { useSyncExternalStore } from 'react';

import type {
  ApiDenialReason,
  ApprovedIngestItem,
  ExecuteSharepointIngestResponse,
  IngestContractType,
  IngestTypePendingReason,
  FieldError,
  PlanSharepointIngestRequest,
  PlanSharepointIngestResponse,
  SowCandidateShape,
} from '@clm/shared-types';

/**
 * The Ingest planner's state, held outside the component — CLM-67, D-67-6.
 *
 * Production, 11 Sep 2026 (Gerry): "if I switch tab it removes the view, I
 * have to put the SharePoint folder again and run plan again." The surface
 * kept everything — the folder link, the options, the plan, the ticks, the
 * execute in flight and the receipt — in `useState`, and `AppRoutes` mounts
 * one surface per route, so a NavRail click unmounted the component and threw
 * the lot away. Worse, an execute whose receipt arrived while the admin was on
 * another surface resolved into an unmounted component and was dropped: the
 * only copy of that run's failure reasons was lost.
 *
 * So the state lives here, in a module-level singleton the component reads
 * through `useSyncExternalStore`. Returning to `/admin/ingest` shows exactly
 * what was there; a promise started from this store (see `planner-actions.ts`)
 * is owned by the store, not by the component, so its result is written here
 * whether or not the surface is mounted.
 *
 * **In memory only, on purpose.** Nothing here touches `localStorage` or
 * `sessionStorage`: a plan carries SharePoint item ids and web URLs for the
 * whole folder, which must not outlive the tab. A full page refresh clears it
 * (D-67-6), "Start over" clears it, and sign-out calls
 * `resetIngestPlannerStore()` so a shared browser never shows another user's
 * plan.
 *
 * This module imports no API or auth code — `IdentityPanel` imports it for the
 * sign-out reset and must not drag msal into its module graph. The calls that
 * need the API live in `planner-actions.ts`.
 */

/* -------------------------------------------------------------------------- */
/* Shape                                                                      */
/* -------------------------------------------------------------------------- */

/** The three form controls, kept as the user typed them (validation is on submit). */
export interface IngestPlannerForm {
  readonly folder_link: string;
  readonly max_documents: string;
  readonly include_unsigned: boolean;
}

export type PlanPhase =
  | { readonly kind: 'idle' }
  | {
      readonly kind: 'loading';
      /**
       * CLM-122 (R-122-4) — the plan this request will replace, when there was one,
       * so `setPlan` can keep the admin's ticks on its Type pending rows.
       */
      readonly previous?: PlanSharepointIngestResponse | null;
    }
  | { readonly kind: 'session_expired' }
  | {
      readonly kind: 'error';
      /** Which call failed — the heading and the off-contract sentence differ. */
      readonly action: 'plan' | 'execute';
      readonly message: string;
      readonly correlation_id: string | null;
      /**
       * Whether the API declined rather than failed, and which way.
       *
       * `'role'` — the endpoint's own gate: a `403`, or the `404` it answers to
       * anyone without the Contributor or Admin role (R-94-1). The card's role
       * sentence is true for it.
       *
       * A sign-in denial (`reason` on the `403`, 30 Sep 2026) — gateway step 4
       * refused the caller before the route was reached, so *every* call fails
       * and the role sentence is false: a CEQ-CLM-Admin member with no scope
       * group would be told she needs the role she holds. The card shows
       * `ErrorNotice`'s copy for the cause instead.
       *
       * `null` — not a deny.
       */
      readonly denial: 'role' | Exclude<ApiDenialReason, 'action_not_permitted'> | null;
      /** A 400's `field_errors`, listed on the card (CLM-67, D-67-5). */
      readonly field_errors: readonly FieldError[];
      /**
       * A failed execute keeps the plan and the request it was posted from, so
       * the ticked rows survive under the problem card and the admin can try
       * again — or Re-plan — without enumerating the folder anew. A failed
       * plan has nothing to keep.
       */
      readonly kept: {
        readonly plan: PlanSharepointIngestResponse;
        readonly request: PlanSharepointIngestRequest;
      } | null;
    }
  | {
      readonly kind: 'plan';
      readonly plan: PlanSharepointIngestResponse;
      /** The request that produced it — mirrored on execute, re-posted on Re-plan. */
      readonly request: PlanSharepointIngestRequest;
    }
  | {
      readonly kind: 'executing';
      readonly plan: PlanSharepointIngestResponse;
      readonly request: PlanSharepointIngestRequest;
      readonly approved: readonly ApprovedIngestItem[];
    }
  | {
      readonly kind: 'receipt';
      readonly plan: PlanSharepointIngestResponse;
      readonly request: PlanSharepointIngestRequest;
      readonly approved: readonly ApprovedIngestItem[];
      readonly receipt: ExecuteSharepointIngestResponse;
    };

export interface IngestPlannerState {
  readonly form: IngestPlannerForm;
  /** The message under the folder-link input; `null` when there is none. */
  readonly field_error: string | null;
  readonly phase: PlanPhase;
  /**
   * Row keys (`drive_id:item_id`) the admin has ticked on the current plan.
   * Re-derived from `signed_marker` whenever a plan arrives (D-67-2); kept
   * across a failed execute so the ticks survive under the problem card.
   *
   * CLM-122 (R-122-4): a Type pending row (`type_pending_reason` set) is never
   * pre-ticked, signed or not. A tick the admin gave one survives a Re-plan
   * while the fresh plan still offers it as Type pending under the same eTag —
   * no Select all can redo a `no_name_type` tick, so a Re-plan must not lose it.
   * For the same reason a corrected row that files as `no_name_type` keeps its
   * tick while its correction survives (same eTag, still a correctable skip).
   */
  readonly selected: ReadonlySet<string>;
  /** The inline "Ingest n documents under the versions shown?" prompt is open. */
  readonly confirming: boolean;
  /**
   * CLM-71 (D-71-2) — the admin's corrections to `to_skip` rows, keyed by
   * `rowKey`. In the browser only: the plan stays a dry run (`dry_run: true`),
   * and the overrides travel inside `approved[]` on Execute, where the server
   * validates them again. A corrected row is never pre-ticked. Cleared by
   * "Start over"; on a new plan only the rows still present under the same eTag
   * keep theirs.
   */
  readonly overrides: ReadonlyMap<string, RowOverride>;
  /**
   * CLM-115 — rows skipped during *this* plan, so the card can move them the
   * moment the server confirms, without a re-crawl.
   *
   * Deliberately transient, and deliberately not the source of truth: from the
   * next plan onwards the server's `user_skipped` is what the Skipped section
   * renders, and `setPlan` clears this. Nothing durable enters the store, so
   * D-67-6 is untouched — a refresh still loses the plan, and the skip survives
   * because it is on the server, not because the browser remembered it.
   */
  readonly skipped_now: ReadonlySet<string>;
}

/** CLM-71. What the admin corrected on one skipped row, plus what the row should display. */
export interface RowOverride {
  /**
   * The eTag the row was skipped under when the admin corrected it. A Re-plan
   * keeps the correction only while the fresh plan still skips the row under
   * this exact eTag — a changed document is corrected again, never assumed.
   */
  readonly etag: string;
  /** Upper-cased, shape-validated in the editor; the server validates again. */
  readonly sow_number_override?: string;
  /** A `party_key` the `PartyPicker` offered — never typed text (FR-14). */
  readonly party_key_override?: string;
  /**
   * CLM-126 (R-126-12) — set only when the editor's party came from a
   * one-click nearest-match suggestion, not a free pick: which candidate, and
   * at what score. The server credits the suggested-match audit actor only
   * when this `party_key` still equals `party_key_override` at apply time
   * (picking something else afterwards clears it, same as a free pick always
   * has — D-88-4's "the human's final choice wins", extended).
   */
  readonly suggestion_accepted?: { readonly party_key: string; readonly score: number };
  /**
   * CLM-77 — normalised tags to store with the row. Not a correction: a
   * tags-only entry never makes a skipped row executable (`isCorrection`) and
   * rides only on rows the admin ticked.
   */
  readonly tags?: readonly string[];
  readonly display: {
    /** `display_name (party_key)` as the picker rendered it; `null` for a number-only correction. */
    readonly party_label: string | null;
    /** From the picked party's row; the server decides for real (§4C). */
    readonly processing_restricted: boolean;
    /** The typed number's shape, for the weak-shape tag; `null` for a party-only correction. */
    readonly sow_number_shape: SowCandidateShape | null;
    /**
     * CLM-76, CLM-118. The type the row files as: the planner's `derived_type`, or SOW from the typed
     * number; `null` when none yet. Display only — nobody picks a type (R-118-18).
     */
    readonly contract_type?: IngestContractType | null;
    /**
     * CLM-122 (R-122-7) — set when the correction files the row as Type pending
     * (`OTHER`, for the AI to type after filing). Display only; the server decides
     * for the picked client.
     */
    readonly type_pending_reason?: IngestTypePendingReason;
  };
}

export const rowKey = (doc: { readonly drive_id: string; readonly item_id: string }): string =>
  `${doc.drive_id}:${doc.item_id}`;

/** CLM-77 — an override that actually corrects the row; a tags-only entry is not one. */
export function isCorrection(override: RowOverride): boolean {
  return override.sow_number_override !== undefined || override.party_key_override !== undefined;
}

/** CLM-77 — the tags held for a row, `[]` when none. */
export function rowTags(overrides: ReadonlyMap<string, RowOverride>, key: string): readonly string[] {
  return overrides.get(key)?.tags ?? [];
}

const EMPTY_FORM: IngestPlannerForm = { folder_link: '', max_documents: '', include_unsigned: false };

const INITIAL_STATE: IngestPlannerState = {
  form: EMPTY_FORM,
  field_error: null,
  phase: { kind: 'idle' },
  selected: new Set(),
  confirming: false,
  overrides: new Map(),
  skipped_now: new Set<string>(),
};

/* -------------------------------------------------------------------------- */
/* The singleton                                                              */
/* -------------------------------------------------------------------------- */

let state: IngestPlannerState = INITIAL_STATE;
const listeners = new Set<() => void>();

/**
 * The one in-flight request, if any. Held by the store — never by the
 * component — so unmounting the surface does not abort or orphan it. A new
 * request, "Start over" and the sign-out reset abort it.
 */
let inflight: AbortController | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

/** Replace the state with a new object (the snapshot must change identity). */
function set(next: IngestPlannerState): void {
  state = next;
  emit();
}

export function getIngestPlannerState(): IngestPlannerState {
  return state;
}

export function subscribeIngestPlanner(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The store as a React hook; re-renders on every change, snapshot is the state object. */
export function useIngestPlannerStore(): IngestPlannerState {
  return useSyncExternalStore(subscribeIngestPlanner, getIngestPlannerState, getIngestPlannerState);
}

/**
 * Abort whatever is in flight and start a fresh controller for the next call.
 * Returns the controller the caller must pass to the API and check before
 * writing a result — a stale controller's result is ignored, not written.
 */
export function beginRequest(): AbortController {
  inflight?.abort();
  const controller = new AbortController();
  inflight = controller;
  return controller;
}

/** True when `controller` is still the one in flight — its result may be written. */
export function isCurrentRequest(controller: AbortController): boolean {
  return inflight === controller && !controller.signal.aborted;
}

/**
 * Everything back to the empty page, any request in flight aborted. Called by
 * "Start over" on the surface and by sign-out (`IdentityPanel`), and by the
 * test suites between tests. In-memory only — there is nothing on disk to
 * clear (D-67-6).
 */
export function resetIngestPlannerStore(): void {
  inflight?.abort();
  inflight = null;
  set(INITIAL_STATE);
}

/* -------------------------------------------------------------------------- */
/* Mutators — the form                                                        */
/* -------------------------------------------------------------------------- */

export function setFolderLink(folder_link: string): void {
  set({ ...state, form: { ...state.form, folder_link } });
}

export function setMaxDocuments(max_documents: string): void {
  set({ ...state, form: { ...state.form, max_documents } });
}

export function setIncludeUnsigned(include_unsigned: boolean): void {
  set({ ...state, form: { ...state.form, include_unsigned } });
}

export function setFieldError(field_error: string | null): void {
  set({ ...state, field_error });
}

/* -------------------------------------------------------------------------- */
/* Mutators — the phase                                                       */
/* -------------------------------------------------------------------------- */

/** Any phase that carries no fresh plan: the selection is left alone. */
export function setPhase(phase: Exclude<PlanPhase, { kind: 'plan' }>): void {
  set({ ...state, phase, confirming: false });
}

/**
 * CLM-122 — the plan the store currently shows, if any: the one a new plan
 * replaces. A plan, an execute in flight or its receipt, a failed execute that
 * kept its plan, or the plan a request in flight will replace.
 */
export function replacedPlanOf(phase: PlanPhase): PlanSharepointIngestResponse | null {
  switch (phase.kind) {
    case 'plan':
    case 'executing':
    case 'receipt':
      return phase.plan;
    case 'error':
      return phase.kept?.plan ?? null;
    case 'loading':
      return phase.previous ?? null;
    default:
      return null;
  }
}

/**
 * A plan arrived. The default tick is the signed marker, derived from the plan
 * (D-67-2) — a row planned without it is never pre-ticked — and the confirm
 * prompt, if open, is closed: it referred to a plan that no longer exists.
 *
 * CLM-122 (R-122-4): a Type pending row is never pre-ticked. It stays ticked
 * only when the admin ticked it on the plan being replaced, where it was offered
 * as Type pending under the same eTag; a changed document is chosen again. A
 * ticked correction that files as `no_name_type` stays ticked while the
 * correction itself is kept.
 */
export function setPlan(plan: PlanSharepointIngestResponse, request: PlanSharepointIngestRequest): void {
  /**
   * CLM-71 — a correction survives a Re-plan only for a row the fresh plan still
   * skips as correctable under the **same eTag**; everything else is dropped
   * (a different folder clears them all). Never pre-ticked, and its tick is not
   * carried — except a kept correction that files as `no_name_type` (CLM-122,
   * below), whose tick no Select all can redo.
   */
  const stillSkipped = new Map(
    plan.to_skip.filter((doc) => doc.correctable && doc.etag !== null).map((doc) => [rowKey(doc), doc.etag]),
  );
  /* CLM-118 — a planned row carries no correction to keep: the reviewer type override is retired (R-118-18). */
  const overrides = new Map<string, RowOverride>();
  for (const [key, override] of state.overrides) {
    if (stillSkipped.get(key) === override.etag) overrides.set(key, override);
  }
  /* CLM-122 (R-122-4) — the Type pending rows the admin had ticked, by key and eTag. */
  const replaced = replacedPlanOf(state.phase);
  const tickedTypePending = new Map(
    (replaced?.to_ingest ?? [])
      .filter((doc) => doc.type_pending_reason !== null && state.selected.has(rowKey(doc)))
      .map((doc) => [rowKey(doc), doc.etag]),
  );
  const preTicked = (doc: PlanSharepointIngestResponse['to_ingest'][number]): boolean =>
    doc.type_pending_reason === null ? doc.signed_marker : tickedTypePending.get(rowKey(doc)) === doc.etag;
  /*
   * CLM-122 (R-122-4) — a kept correction that files as `no_name_type` keeps the admin's tick: it is out
   * of every Select all, so a dropped tick could only be given again by hand. Its eTag matched above;
   * like a planned row's, the tick is carried only from the plan being replaced. Any other corrected
   * row is re-ticked by its group's Select all, as CLM-71 left it.
   */
  const replacedSkips = new Set((replaced?.to_skip ?? []).map(rowKey));
  const tickedNoNameTypeCorrections = [...overrides]
    .filter(
      ([key, override]) =>
        override.display.type_pending_reason === 'no_name_type' && state.selected.has(key) && replacedSkips.has(key),
    )
    .map(([key]) => key);
  set({
    ...state,
    phase: { kind: 'plan', plan, request },
    selected: new Set([...plan.to_ingest.filter(preTicked).map(rowKey), ...tickedNoNameTypeCorrections]),
    confirming: false,
    overrides,
    /* CLM-115 — cleared on every plan. The fresh `user_skipped` already holds
     * everything skipped before this crawl, so keeping the optimistic set would
     * only risk showing a row as skipped that the server did not record. */
    skipped_now: new Set<string>(),
  });
}

/**
 * CLM-115. Mark one row skipped for the rest of this plan.
 *
 * Called only after the server has confirmed the write, so the card never shows
 * a row as settled that is not. Also drops the row's tick and any correction:
 * a skipped document must not travel in `approved[]`, and leaving a correction
 * behind would put it back in the corrected group on the next render.
 */
export function markSkipped(key: string): void {
  const skipped_now = new Set(state.skipped_now);
  skipped_now.add(key);
  const selected = new Set(state.selected);
  selected.delete(key);
  const overrides = new Map(state.overrides);
  overrides.delete(key);
  set({ ...state, skipped_now, selected, overrides, confirming: false });
}

/* -------------------------------------------------------------------------- */
/* Mutators — the ticks                                                       */
/* -------------------------------------------------------------------------- */

/** Tick or clear one row or a whole group — the same operation, `keys` decides. */
export function selectRows(keys: readonly string[], on: boolean): void {
  const next = new Set(state.selected);
  for (const key of keys) {
    if (on) next.add(key);
    else next.delete(key);
  }
  set({ ...state, selected: next });
}

export function setConfirming(confirming: boolean): void {
  set({ ...state, confirming });
}

/* -------------------------------------------------------------------------- */
/* Mutators — the corrections (CLM-71)                                        */
/* -------------------------------------------------------------------------- */

/**
 * Set or clear one row's correction. Setting never ticks the row (D-71 — the
 * same rule as an unsigned candidate); clearing also un-ticks it, so a row
 * returned to the skip card cannot stay in the approved set.
 */
export function setOverride(key: string, override: RowOverride | null): void {
  const overrides = new Map(state.overrides);
  if (override === null) {
    overrides.delete(key);
    const selected = new Set(state.selected);
    selected.delete(key);
    set({ ...state, overrides, selected, confirming: false });
    return;
  }
  overrides.set(key, override);
  set({ ...state, overrides, confirming: false });
}
